'use client';

import styles from './LeftNavEntityList.module.css';
import {
  Flex,
  Text,
  Input,
  ButtonGroup,
  Button,
  Icon,
  Flair,
} from '@/_components';
import { LeftNavEntityListProps } from './LeftNavEntityList.types';
import { UserContext } from '@/_contexts/UserProvider';
import { useContext, useState, type ChangeEvent, type FC } from 'react';
import { useParams, useRouter } from 'next/navigation';
import { getClasses } from '@/_utils/classnames';

const LIMIT_ENTITY_COUNT = 5;

export const LeftNavEntityList: FC<LeftNavEntityListProps> = ({
  companies,
  selectedCompanyId,
  entityUnseenCounts,
}) => {
  const [filter, setFilter] = useState<string>('');
  const [isLimit, setLimit] = useState<boolean>(true);

  const { previewUserId } = useParams();

  const onFilterChange = (event: ChangeEvent<HTMLInputElement>) =>
    setFilter(event.target.value);

  const { push } = useRouter();
  const { updateClientCompany } = useContext(UserContext);

  if (!companies?.length) return null;

  const searchFilteredCompanies = companies?.filter((c) => {
    const cName: string = c.name.toLowerCase();
    const cType: string = c.entity_type.toLowerCase();
    const filterLowercase: string = filter.toLowerCase();

    return cName.includes(filterLowercase) || cType.includes(filterLowercase);
  });

  const isFilterEnabled: boolean = companies?.length > LIMIT_ENTITY_COUNT;

  const limitedCompanies = isLimit
    ? searchFilteredCompanies.slice(0, LIMIT_ENTITY_COUNT)
    : searchFilteredCompanies;

  const onToggleLimit = () => setLimit(!isLimit);

  const onClearFilter = () => setFilter('');

  return (
    <Flex isColumn={true} gap={2}>
      {isFilterEnabled && (
        <Input
          onChange={onFilterChange}
          value={filter}
          label="Filter"
          autoComplete="off"
          iconRight={!!filter ? 'x' : undefined}
          onRightClick={onClearFilter}
          isLabelHidden={true}
          placeholder="Filter"
        />
      )}
      {limitedCompanies.map((company, key) => {
        const onActivateCompany = () => {
          !!previewUserId
            ? push(
                `/preview/user/${previewUserId}?previewCompanyHash=${company.hash_id}`,
              )
            : updateClientCompany(company);
        };

        const isSelected: boolean = selectedCompanyId === company.id;
        const unseenCount = entityUnseenCounts[company.hash_id] || 0;

        const LeftNavEntityListClasses: string = getClasses(
          styles.leftNavEntityListItem,
          {
            [styles.leftNavEntityListItemActive]: isSelected,
          },
        );
        return (
          <Flex
            isColumn={true}
            onClick={onActivateCompany}
            padding={4}
            key={key}
            className={LeftNavEntityListClasses}
          >
            <Flex isYCentered={true} isBetween={true}>
              <Text
                type="small"
                fontWeight="medium"
                color={isSelected ? 'accent' : undefined}
              >
                {company.name}
              </Text>
              <Flex isYCentered={true} gap={4}>
                {!isSelected && unseenCount > 0 && (
                  <Flair count={unseenCount} />
                )}
                {isSelected && (
                  <Icon name="check-circle" size={16} color="accent" />
                )}
              </Flex>
            </Flex>
            <Text type="tiny" color="secondary">
              {company.entity_type}
            </Text>
          </Flex>
        );
      })}
      {isFilterEnabled && isLimit && !filter && (
        <ButtonGroup isFullWidth={true}>
          <Button
            value="Show All"
            onClick={onToggleLimit}
            intent="secondary"
            size="small"
          />
        </ButtonGroup>
      )}
    </Flex>
  );
};
